import React, { useState, useEffect, useRef } from 'react';
import { Cloud, Shield, GitBranch, Server, Code, Headphones, Mail, Phone, MapPin, Menu, X, ChevronRight, Clock, GraduationCap, Laptop, Users, Briefcase, Sun, Moon, PhoneCall, Globe, Network, Facebook, Linkedin, Instagram, Twitter, Youtube, MessageCircle, Target } from 'lucide-react';
import { companyData } from '../mock';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { privacyContent } from '../data/privacy-content';
import NetworkGlobe from './NetworkGlobe';
import InteractiveNetworkGlobe from './InteractiveNetworkGlobe';
import AIChat from './AIChat';
import SecurityRiskAssessment from './SecurityRiskAssessment';
import FAQSection from './FAQSection';
import HelpWidget from './HelpWidget';

const iconMap = {
  Cloud,
  Shield,
  GitBranch,
  Server,
  Code,
  Headphones,
  Laptop,
  Phone: PhoneCall,
  Users,
  Target
};

const careerIconMap = {
  Clock,
  GraduationCap,
  Laptop,
  Users
};

const TetrisBlock = ({ delay, duration, left, color }) => (
  <div
    className="tetris-block"
    style={{
      left: `${left}%`,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
      backgroundColor: color
    }}
  />
);

const LandingPage = () => {
  const { t, i18n, ready } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    helpType: '', 
    message: '' 
  });
  const [formStatus, setFormStatus] = useState({ loading: false, success: false, error: null });
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showManagedITModal, setShowManagedITModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showCloudModal, setShowCloudModal] = useState(false);
  const [showWorkplaceModal, setShowWorkplaceModal] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [showSecurityIncidentModal, setShowSecurityIncidentModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [applicationData, setApplicationData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    position: '', 
    message: '',
    resume: null 
  });
  const [applicationStatus, setApplicationStatus] = useState({ loading: false, success: false, error: null });
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showInterimModal, setShowInterimModal] = useState(false);
  const [showImprintModal, setShowImprintModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [projectCount, setProjectCount] = useState(0);
  const [yearsCount, setYearsCount] = useState(0);
  const [clientsCount, setClientsCount] = useState(0);
  const [uptimeCount, setUptimeCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [showSecurityAssessment, setShowSecurityAssessment] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const aiChatRef = useRef(null);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Check cookie consent on mount
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowCookieBanner(true);
    }
  }, []);

  // Initialize theme from localStorage or default to dark
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.setAttribute('data-theme', savedTheme);
    } else {
      // Default to dark mode
      setTheme('dark');
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'all');
    setShowCookieBanner(false);
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('cookieConsent', 'necessary');
    setShowCookieBanner(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend Validation
    const errors = [];
    
    // Name validation
    if (!formData.name || formData.name.trim().length < 2) {
      errors.push('Bitte geben Sie einen gültigen Namen ein (mindestens 2 Zeichen)');
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      errors.push('Bitte geben Sie eine gültige E-Mail-Adresse ein');
    }
    
    // Message validation
    if (!formData.message || formData.message.trim().length < 10) {
      errors.push('Bitte geben Sie eine Nachricht ein (mindestens 10 Zeichen)');
    }
    
    // If validation fails, show error
    if (errors.length > 0) {
      setFormStatus({ 
        loading: false, 
        success: false, 
        error: errors.join('. ')
      });
      return;
    }
    
    setFormStatus({ loading: true, success: false, error: null });
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: formData.subject?.trim() || 'Kontaktanfrage',
          message: formData.message.trim(),
          phone: formData.phone?.trim() || null,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fehler beim Senden der Nachricht');
      }
      
      const result = await response.json();
      setFormStatus({ loading: false, success: true, error: null });
      setFormData({ name: '', email: '', subject: '', message: '', phone: '' });
      
      // Show success message for 5 seconds
      setTimeout(() => {
        setFormStatus({ loading: false, success: false, error: null });
      }, 5000);
      
    } catch (error) {
      console.error('Fehler beim Senden:', error);
      setFormStatus({ 
        loading: false, 
        success: false, 
        error: error.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.' 
      });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  // Application Modal Handlers
  const openApplicationModal = (position) => {
    setSelectedPosition(position);
    setApplicationData({ ...applicationData, position });
    setShowApplicationModal(true);
    setApplicationStatus({ loading: false, success: false, error: null });
  };

  const closeApplicationModal = () => {
    setShowApplicationModal(false);
    setApplicationData({ name: '', email: '', phone: '', position: '', message: '', resume: null });
    setApplicationStatus({ loading: false, success: false, error: null });
  };

  const handleApplicationChange = (e) => {
    setApplicationData({ ...applicationData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setApplicationStatus({ 
          loading: false, 
          success: false, 
          error: t('careers.applicationForm.fileError') 
        });
        e.target.value = null;
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setApplicationStatus({ 
          loading: false, 
          success: false, 
          error: t('careers.applicationForm.fileError') 
        });
        e.target.value = null;
        return;
      }
      
      setApplicationData({ ...applicationData, resume: file });
      setApplicationStatus({ loading: false, success: false, error: null });
    }
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!applicationData.name || !applicationData.email || !applicationData.phone || !applicationData.message || !applicationData.resume) {
      setApplicationStatus({ 
        loading: false, 
        success: false, 
        error: t('careers.applicationForm.requiredFields') 
      });
      return;
    }
    
    setApplicationStatus({ loading: true, success: false, error: null });
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const formDataToSend = new FormData();
      formDataToSend.append('name', applicationData.name);
      formDataToSend.append('email', applicationData.email);
      formDataToSend.append('phone', applicationData.phone);
      formDataToSend.append('position', applicationData.position);
      formDataToSend.append('message', applicationData.message);
      formDataToSend.append('resume', applicationData.resume);
      
      const response = await fetch(`${backendUrl}/api/application`, {
        method: 'POST',
        body: formDataToSend,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('careers.applicationForm.error'));
      }
      
      const result = await response.json();
      setApplicationStatus({ loading: false, success: true, error: null });
      
      // Close modal after 3 seconds
      setTimeout(() => {
        closeApplicationModal();
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting application:', error);
      setApplicationStatus({ 
        loading: false, 
        success: false, 
        error: error.message || t('careers.applicationForm.error')
      });
    }
  };

  // Counter Animation for Projects
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateCounter();
          }
        });
      },
      { threshold: 0.5 }
    );

    const statsElement = document.querySelector('.stats-grid');
    if (statsElement) {
      observer.observe(statsElement);
    }

    return () => {
      if (statsElement) {
        observer.unobserve(statsElement);
      }
    };
  }, [hasAnimated]);

  const animateCounter = () => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    
    // Projects counter (0-500)
    let projectCurrent = 0;
    const projectIncrement = 500 / steps;
    const projectTimer = setInterval(() => {
      projectCurrent += projectIncrement;
      if (projectCurrent >= 500) {
        setProjectCount(500);
        clearInterval(projectTimer);
      } else {
        setProjectCount(Math.floor(projectCurrent));
      }
    }, duration / steps);
    
    // Years counter (0-15)
    let yearsCurrent = 0;
    const yearsIncrement = 15 / steps;
    const yearsTimer = setInterval(() => {
      yearsCurrent += yearsIncrement;
      if (yearsCurrent >= 15) {
        setYearsCount(15);
        clearInterval(yearsTimer);
      } else {
        setYearsCount(Math.floor(yearsCurrent));
      }
    }, duration / steps);
    
    // Clients counter (0-200)
    let clientsCurrent = 0;
    const clientsIncrement = 200 / steps;
    const clientsTimer = setInterval(() => {
      clientsCurrent += clientsIncrement;
      if (clientsCurrent >= 200) {
        setClientsCount(200);
        clearInterval(clientsTimer);
      } else {
        setClientsCount(Math.floor(clientsCurrent));
      }
    }, duration / steps);
    
    // Uptime counter (0-99.9)
    let uptimeCurrent = 0;
    const uptimeIncrement = 99.9 / steps;
    const uptimeTimer = setInterval(() => {
      uptimeCurrent += uptimeIncrement;
      if (uptimeCurrent >= 99.9) {
        setUptimeCount(99.9);
        clearInterval(uptimeTimer);
      } else {
        setUptimeCount(parseFloat(uptimeCurrent.toFixed(1)));
      }
    }, duration / steps);
  };

  // Don't render until translations are loaded
  if (!ready) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#000000'
      }}>
        {/* Loading screen - waiting for translations */}
      </div>
    );
  }

  return (
    <div className="landing-page">
      {/* Cookie Consent Banner */}
      {showCookieBanner && (
        <div className="cookie-banner-overlay">
          <div className="cookie-banner">
            <div className="cookie-content">
              <h3 className="cookie-title">{t('cookie.title')}</h3>
              <p className="cookie-text">
                {t('cookie.description')}
              </p>
              <div className="cookie-details">
                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <strong>{t('cookie.necessary')}</strong>
                    <span className="cookie-required">{t('cookie.required')}</span>
                  </div>
                  <p className="cookie-category-desc">
                    {t('cookie.necessaryDescription')}
                  </p>
                </div>
                <div className="cookie-category">
                  <div className="cookie-category-header">
                    <strong>{t('cookie.analytics')}</strong>
                    <span className="cookie-optional">{t('cookie.optional')}</span>
                  </div>
                  <p className="cookie-category-desc">
                    {t('cookie.analyticsDescription')}
                  </p>
                </div>
              </div>
              <div className="cookie-buttons">
                <Button className="btn-secondary" onClick={handleAcceptNecessary}>
                  {t('cookie.onlyNecessary')}
                </Button>
                <Button className="btn-primary" onClick={handleAcceptAll}>
                  {t('cookie.acceptAll')}
                </Button>
              </div>
              <div className="cookie-links">
                <button onClick={() => setShowPrivacyModal(true)} className="cookie-link cookie-link-button">{t('cookie.privacy')}</button>
                <span>•</span>
                <button onClick={() => setShowImprintModal(true)} className="cookie-link cookie-link-button">{t('cookie.imprint')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animated Tetris Blocks Background */}
      {/* Tetris background disabled for better scroll performance */}
      {/* 
      <div className="tetris-background">
        <TetrisBlock delay={0} duration={15} left={5} color="#0099FF" />
        <TetrisBlock delay={3} duration={18} left={15} color="#FF006E" />
        <TetrisBlock delay={6} duration={20} left={30} color="#8338EC" />
        <TetrisBlock delay={2} duration={16} left={50} color="#FFB703" />
        <TetrisBlock delay={8} duration={19} left={70} color="#3AA8FF" />
        <TetrisBlock delay={5} duration={17} left={85} color="#0099FF" />
      </div>
      */}

      {/* Header */}
      <header className="dark-header">
        <div className="header-content">
          <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/32p3ysgt_AB-INFORMATIK-Logo_Verlauf_2025%20neu.png" alt="AB-Informatik Logo" className="dark-logo logo-dark-mode" />
          <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/dw1boizi_AB-INFORMATIK-Logo_Verlauf_2025.png" alt="AB-Informatik Logo" className="dark-logo logo-light-mode" />
          
          {/* Desktop Navigation */}
          <nav className="dark-nav desktop-nav">
            <button onClick={() => scrollToSection('security-banner')} className="dark-nav-link">{t('nav.actions')}</button>
            <button onClick={() => scrollToSection('services')} className="dark-nav-link">{t('nav.services')}</button>
            <button onClick={() => scrollToSection('about')} className="dark-nav-link">{t('nav.company')}</button>
            <button onClick={() => scrollToSection('careers')} className="dark-nav-link">{t('nav.careers')}</button>
            <button onClick={() => scrollToSection('testimonials')} className="dark-nav-link">{t('nav.testimonials')}</button>
            
            {/* Language Switcher */}
            <div className="language-switcher">
              <button 
                onClick={() => changeLanguage('de')} 
                className={`lang-btn ${i18n.language === 'de' ? 'active' : ''}`}
              >
                DE
              </button>
              <span className="lang-divider">|</span>
              <button 
                onClick={() => changeLanguage('en')} 
                className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
              >
                EN
              </button>
            </div>

            {/* Social Media Icons */}
            <div className="social-media-wrapper">
              <div className="social-media-icons">
                <a href="#" className="social-icon" aria-label="Facebook">
                  <Facebook size={18} />
                </a>
                <a href="#" className="social-icon" aria-label="LinkedIn">
                  <Linkedin size={18} />
                </a>
                <a href="#" className="social-icon" aria-label="Instagram">
                  <Instagram size={18} />
                </a>
                <a href="#" className="social-icon" aria-label="Twitter">
                  <Twitter size={18} />
                </a>
                <a href="#" className="social-icon" aria-label="YouTube">
                  <Youtube size={18} />
                </a>
                <a href="#" className="social-icon" aria-label="Xing">
                  <svg className="xing-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.188 0c-.517 0-.741.325-.927.66 0 0-7.455 13.224-7.702 13.657.015.024 4.919 9.023 4.919 9.023.17.308.436.66.967.66h3.454c.211 0 .375-.078.463-.22.089-.151.089-.346-.009-.536l-4.879-8.916c-.004-.006-.004-.016 0-.022L22.139.756c.095-.191.097-.387.006-.535C22.056.078 21.894 0 21.686 0h-3.498zM3.648 4.74c-.211 0-.385.074-.473.216-.09.149-.078.339.02.531l2.34 4.05c.004.01.004.016 0 .021L1.86 16.051c-.099.188-.093.381 0 .529.085.142.239.234.45.234h3.461c.518 0 .766-.348.945-.667l3.734-6.609-2.378-4.155c-.172-.315-.434-.659-.962-.659H3.648v.016z"/>
                  </svg>
                </a>
              </div>
              <span className="coming-soon-badge">Coming Soon!</span>
            </div>
            
            <button onClick={toggleTheme} className="theme-toggle" aria-label="Toggle theme">
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <Button className="btn-secondary" onClick={() => scrollToSection('contact')}>
              {t('nav.contact')}
            </Button>
          </nav>

          {/* Mobile Menu Button & Theme Toggle */}
          <div className="mobile-header-actions">
            <button onClick={toggleTheme} className="theme-toggle mobile-theme-toggle" aria-label="Toggle theme">
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <button onClick={() => scrollToSection('security-banner')} className="mobile-nav-link">{t('nav.actions')}</button>
            <button onClick={() => scrollToSection('services')} className="mobile-nav-link">{t('nav.services')}</button>
            <button onClick={() => scrollToSection('about')} className="mobile-nav-link">{t('nav.company')}</button>
            <button onClick={() => scrollToSection('careers')} className="mobile-nav-link">{t('nav.careers')}</button>
            <button onClick={() => scrollToSection('testimonials')} className="mobile-nav-link">{t('nav.testimonials')}</button>
            
            {/* Contact Button */}
            <Button 
              className="btn-secondary" 
              onClick={() => {
                scrollToSection('contact');
                setMobileMenuOpen(false);
              }}
              style={{ 
                width: '100%', 
                marginTop: '16px',
                justifyContent: 'center'
              }}
            >
              <Mail size={18} />
              {t('nav.contact')}
            </Button>
            
            {/* IT Security Incident Button */}
            <Button 
              className="btn-primary mobile-security-btn" 
              onClick={() => {
                setShowSecurityIncidentModal(true);
                setMobileMenuOpen(false);
              }}
              style={{ 
                width: '100%', 
                marginTop: '12px',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #ff3b30, #ff6b58) !important'
              }}
            >
              <Shield size={18} />
              {t('nav.securityIncident')}
            </Button>
            
            {/* Mobile Language Switcher */}
            <div className="mobile-language-switcher">
              <button 
                onClick={() => changeLanguage('de')} 
                className={`lang-btn ${i18n.language === 'de' ? 'active' : ''}`}
              >
                DE
              </button>
              <span className="lang-divider">|</span>
              <button 
                onClick={() => changeLanguage('en')} 
                className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
              >
                EN
              </button>
            </div>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        {/* Galaxy Starfield Background - Dark Mode Desktop Only */}
        <div className="starfield-background">
          <div className="stars stars-layer-1"></div>
          <div className="stars stars-layer-2"></div>
          <div className="stars stars-layer-3"></div>
        </div>
        
        {/* Interactive Network Globe Visualization */}
        <InteractiveNetworkGlobe 
          isDarkMode={theme === 'dark'} 
          onServiceClick={(service) => {
            // Open corresponding service modal
            if (service.title.includes('Managed IT')) setShowManagedITModal(true);
            else if (service.title.includes('Security')) setShowSecurityModal(true);
            else if (service.title.includes('Cloud')) setShowCloudModal(true);
            else if (service.title.includes('Workplace')) setShowWorkplaceModal(true);
            else if (service.title.includes('Netzwerk')) setShowNetworkModal(true);
            else if (service.title.includes('Strategie')) setShowStrategyModal(true);
            else if (service.title.includes('Telefon')) setShowPhoneModal(true);
            else if (service.title.includes('Support')) setShowSupportModal(true);
          }}
        />
        
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="display-huge">{t('hero.title')}</h1>
            <p className="body-large">{t('hero.description')}</p>
            <div className="hero-buttons">
              <Button className="btn-primary" onClick={() => setShowSecurityAssessment(true)}>
                <Shield size={20} />
                Kostenloses Security-Assessment
              </Button>
              <Button className="btn-secondary" onClick={() => scrollToSection('services')}>
                {t('hero.discoverServices')}
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{projectCount}+</div>
              <div className="stat-label">{t('stats.completedProjects')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{yearsCount}+</div>
              <div className="stat-label">{t('stats.yearsExperience')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{clientsCount}+</div>
              <div className="stat-label">{t('stats.satisfiedClients')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{uptimeCount}%</div>
              <div className="stat-label">{t('stats.uptime')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Check Banner */}
      <section id="security-banner" className="security-banner-section">
        <div className="section-content">
          <div className="security-banner">
            <div className="security-banner-badge">
              <Shield size={16} />
              {t('securityBanner.badge')}
            </div>
            <div className="security-banner-content">
              <div className="security-banner-text">
                <h2 className="display-medium">{t('securityBanner.title')}</h2>
                <p className="body-large">
                  {t('securityBanner.description')}
                </p>
                <div className="security-features">
                  <div className="security-feature">
                    <div className="feature-icon">✓</div>
                    <span>{t('securityBanner.features.onsite')}</span>
                  </div>
                  <div className="security-feature">
                    <div className="feature-icon">✓</div>
                    <span>{t('securityBanner.features.report')}</span>
                  </div>
                  <div className="security-feature">
                    <div className="feature-icon">✓</div>
                    <span>{t('securityBanner.features.consultation')}</span>
                  </div>
                </div>
              </div>
              <div className="security-banner-action">
                <Button 
                  className="btn-primary btn-large" 
                  onClick={() => scrollToSection('contact')}
                >
                  {t('securityBanner.cta')}
                  <ChevronRight size={20} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure Check Banner */}
      <section className="security-banner-section">
        <div className="section-content">
          <div className="security-banner">
            <div className="security-banner-badge">
              <Server size={16} />
              {t('infrastructureBanner.badge')}
            </div>
            <div className="security-banner-content">
              <div className="security-banner-text">
                <h2 className="display-medium">{t('infrastructureBanner.title')}</h2>
                <p className="body-large">
                  {t('infrastructureBanner.description')}
                </p>
                <div className="security-features">
                  <div className="security-feature">
                    <Shield size={20} />
                    <span>{t('infrastructureBanner.features.analysis')}</span>
                  </div>
                  <div className="security-feature">
                    <GitBranch size={20} />
                    <span>{t('infrastructureBanner.features.recommendations')}</span>
                  </div>
                  <div className="security-feature">
                    <Headphones size={20} />
                    <span>{t('infrastructureBanner.features.consultation')}</span>
                  </div>
                </div>
              </div>
              <div className="security-banner-cta">
                <Button 
                  className="btn-primary btn-large"
                  onClick={() => scrollToSection('contact')}
                >
                  {t('infrastructureBanner.cta')}
                  <ChevronRight size={20} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Risk Assessment CTA Section */}
      <section className="security-assessment-cta-section" style={{
        padding: '60px 20px',
        background: 'linear-gradient(135deg, rgba(0, 113, 227, 0.1), rgba(0, 168, 255, 0.1))',
        borderTop: '1px solid var(--border-subtle)',
        borderBottom: '1px solid var(--border-subtle)'
      }}>
        <div className="section-content" style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            background: 'linear-gradient(135deg, #0071e3, #00a8ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: '14px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            marginBottom: '20px'
          }}>
            ✓ Kostenlos · 2 Minuten · Sofort-Ergebnis
          </div>
          <h2 className="display-medium" style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
            Wie sicher ist Ihre IT wirklich?
          </h2>
          <p className="body-large" style={{ marginBottom: '32px', color: 'var(--text-secondary)' }}>
            Finden Sie in nur 2 Minuten heraus, welche Sicherheitsrisiken in Ihrer IT-Infrastruktur lauern. 
            Erhalten Sie eine konkrete Bewertung und personalisierte Handlungsempfehlungen – völlig kostenlos.
          </p>
          <Button 
            className="btn-primary btn-large"
            onClick={() => setShowSecurityAssessment(true)}
            style={{
              padding: '18px 40px',
              fontSize: '18px',
              boxShadow: '0 8px 24px rgba(0, 113, 227, 0.3)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <Shield size={24} />
            Jetzt kostenloses Security-Assessment starten
            <ChevronRight size={24} />
          </Button>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-content">
          <h2 className="display-large section-title">{t('services.title')}</h2>
          <p className="body-medium section-subtitle">{t('services.subtitle')}</p>
          
          <div className="services-grid">
            {companyData.services.map((service) => {
              const Icon = iconMap[service.icon];
              const isSupport = service.id === 8;
              const isManagedIT = service.id === 1;
              const isSecurity = service.id === 2;
              const isCloud = service.id === 3;
              const isWorkplace = service.id === 4;
              const isNetwork = service.id === 5;
              const isPhone = service.id === 6;
              const isInterim = service.id === 7;
              const isStrategy = service.id === 9;
              
              const serviceKeys = ['managedIT', 'security', 'cloud', 'workplace', 'network', 'phone', 'interim', 'support', 'strategy'];
              const translationKey = serviceKeys[service.id - 1];
              
              // Feature highlights for each service (bullet points)
              const serviceFeatures = {
                1: ['Proaktives Monitoring', 'Regelmäßige Wartung', 'Software-Updates'],
                2: ['24/7-Security-Monitoring', 'NIS2-Compliance', 'Penetrationstests'],
                3: ['Microsoft 365', 'DSGVO-konforme Rechenzentren', 'Hybrid-Cloud'],
                4: ['Teams & Collaboration', 'Mobile Device Management', 'Zero-Trust-Security'],
                5: ['Netzwerk-Design', 'WLAN-Optimierung', 'VPN & SD-WAN'],
                6: ['VoIP-Telefonie', 'Cloud-basiert', 'Mobil & flexibel'],
                7: ['IT-Interim-Management', 'Projektleitung', 'Change-Management'],
                8: ['30 Min Reaktionszeit', '24/7 erreichbar', 'Vor-Ort-Service'],
                9: ['Digitalisierungs-Roadmap', 'Technologie-Bewertung', 'Budget-Planung']
              };
              
              const features = serviceFeatures[service.id] || [];
              
              return (
                <div 
                  key={service.id} 
                  className="service-card-modern"
                  onClick={() => {
                    if (isSupport) setShowSupportModal(true);
                    if (isManagedIT) setShowManagedITModal(true);
                    if (isSecurity) setShowSecurityModal(true);
                    if (isCloud) setShowCloudModal(true);
                    if (isWorkplace) setShowWorkplaceModal(true);
                    if (isNetwork) setShowNetworkModal(true);
                    if (isPhone) setShowPhoneModal(true);
                    if (isInterim) setShowInterimModal(true);
                    if (isStrategy) setShowStrategyModal(true);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="service-card-header">
                    <div className="service-icon-modern">
                      <Icon size={28} />
                    </div>
                    <h3 className="service-card-title">{t(`services.items.${translationKey}.title`)}</h3>
                  </div>
                  
                  <ul className="service-features-list">
                    {features.map((feature, idx) => (
                      <li key={idx} className="service-feature-item">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                          <circle cx="8" cy="8" r="8" fill="currentColor" opacity="0.1"/>
                          <path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button className="service-card-cta">
                    <span>Mehr erfahren</span>
                    <ChevronRight size={18} />
                  </button>
                </div>
              );
            })}
          </div>
          
          {/* Support Modal */}
          {showSupportModal && (
            <div className="modal-overlay" onClick={() => setShowSupportModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowSupportModal(false)}>
                  <X size={24} />
                </button>
                <div className="modal-header">
                  <div className="modal-icon">
                    <Headphones size={32} />
                  </div>
                  <h2 className="display-medium">{t('modal.support247.title')}</h2>
                </div>
                <div className="modal-body">
                  <p className="body-medium">
                    {t('modal.support247.description')}
                  </p>
                  <div className="modal-features">
                    <h3 className="heading-3" style={{ marginBottom: '16px' }}>{t('modal.support247.features.title')}</h3>
                    <div className="modal-feature-item">
                      <div className="feature-check">✓</div>
                      <span>{t('modal.support247.features.phone')}</span>
                    </div>
                    <div className="modal-feature-item">
                      <div className="feature-check">✓</div>
                      <span>{t('modal.support247.features.onsite')}</span>
                    </div>
                    <div className="modal-feature-item">
                      <div className="feature-check">✓</div>
                      <span>{t('modal.support247.features.remote')}</span>
                    </div>
                    <div className="modal-feature-item">
                      <div className="feature-check">✓</div>
                      <span>{t('modal.support247.features.response')}</span>
                    </div>
                  </div>
                  <Button 
                    className="btn-primary btn-full" 
                    onClick={() => {
                      setShowSupportModal(false);
                      scrollToSection('contact');
                    }}
                  >
                    {t('modal.support247.contact')}
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Managed IT Modal */}
          {showManagedITModal && (
            <div className="modal-overlay" onClick={() => setShowManagedITModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowManagedITModal(false)}>
                  <X size={24} />
                </button>
                <div className="modal-header">
                  <div className="modal-icon">
                    <Server size={32} />
                  </div>
                  <h2 className="display-medium">{t('modal.managedIT.title')}</h2>
                </div>
                <div className="modal-body">
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.managedIT.description')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.managedIT.paragraph1')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.managedIT.paragraph2')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    {t('modal.managedIT.paragraph3')}
                  </p>
                  <Button 
                    className="btn-primary btn-full" 
                    onClick={() => {
                      setShowManagedITModal(false);
                      scrollToSection('contact');
                    }}
                  >
                    {t('modal.managedIT.contact')}
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Security Modal */}
          {showSecurityModal && (
            <div className="modal-overlay" onClick={() => setShowSecurityModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowSecurityModal(false)}>
                  <X size={24} />
                </button>
                <div className="modal-header">
                  <div className="modal-icon">
                    <Shield size={32} />
                  </div>
                  <h2 className="display-medium">{t('modal.security.title')}</h2>
                </div>
                <div className="modal-body">
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.security.description')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.security.paragraph1')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    {t('modal.security.paragraph2')}
                  </p>
                  <Button 
                    className="btn-primary btn-full" 
                    onClick={() => {
                      setShowSecurityModal(false);
                      scrollToSection('contact');
                    }}
                  >
                    {t('modal.security.contact')}
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Cloud Modal */}
          {showCloudModal && (
            <div className="modal-overlay" onClick={() => setShowCloudModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowCloudModal(false)}>
                  <X size={24} />
                </button>
                <div className="modal-header">
                  <div className="modal-icon">
                    <Cloud size={32} />
                  </div>
                  <h2 className="display-medium">{t('modal.cloud.title')}</h2>
                </div>
                <div className="modal-body">
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.cloud.description')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.cloud.paragraph1')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    {t('modal.cloud.paragraph2')}
                  </p>
                  <Button 
                    className="btn-primary btn-full" 
                    onClick={() => {
                      setShowCloudModal(false);
                      scrollToSection('contact');
                    }}
                  >
                    {t('modal.cloud.contact')}
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Modern Workplace Modal */}
          {showWorkplaceModal && (
            <div className="modal-overlay" onClick={() => setShowWorkplaceModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowWorkplaceModal(false)}>
                  <X size={24} />
                </button>
                <div className="modal-header">
                  <div className="modal-icon">
                    <Laptop size={32} />
                  </div>
                  <h2 className="display-medium">{t('modal.workplace.title')}</h2>
                </div>
                <div className="modal-body">
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.workplace.description')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.workplace.paragraph1')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    {t('modal.workplace.paragraph2')}
                  </p>
                  <Button 
                    className="btn-primary btn-full" 
                    onClick={() => {
                      setShowWorkplaceModal(false);
                      scrollToSection('contact');
                    }}
                  >
                    {t('modal.workplace.contact')}
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Network Management Modal */}
          {showNetworkModal && (
            <div className="modal-overlay" onClick={() => setShowNetworkModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowNetworkModal(false)}>
                  <X size={24} />
                </button>
                <div className="modal-header">
                  <div className="modal-icon">
                    <Network size={32} />
                  </div>
                  <h2 className="display-medium">{t('modal.network.title')}</h2>
                </div>
                <div className="modal-body">
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.network.description')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.network.paragraph1')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    {t('modal.network.paragraph2')}
                  </p>
                  <Button 
                    className="btn-primary btn-full" 
                    onClick={() => {
                      setShowNetworkModal(false);
                      scrollToSection('contact');
                    }}
                  >
                    {t('modal.network.contact')}
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* IT Security Incident Modal */}
          {showSecurityIncidentModal && (
            <div className="modal-overlay" onClick={() => setShowSecurityIncidentModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowSecurityIncidentModal(false)}>
                  <X size={24} />
                </button>
                <div className="modal-header">
                  <div className="modal-icon" style={{ background: 'linear-gradient(135deg, #ff3b30, #ff6b58)' }}>
                    <Shield size={32} />
                  </div>
                  <h2 className="display-medium">{t('nav.securityIncident')}</h2>
                </div>
                <div className="modal-body">
                  <p className="body-medium" style={{ marginBottom: '24px', fontSize: '16px', fontWeight: 500 }}>
                    Bei einem IT-Sicherheitsvorfall erreichen Sie uns sofort:
                  </p>
                  
                  <div style={{ 
                    background: 'var(--bg-tertiary)', 
                    padding: '24px', 
                    borderRadius: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                      <Phone size={28} style={{ color: 'var(--accent-blue)' }} />
                      <div>
                        <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          Notfall-Hotline
                        </div>
                        <a href="tel:+492241261310" style={{ 
                          fontSize: '24px', 
                          fontWeight: 700, 
                          color: 'var(--text-primary)',
                          textDecoration: 'none'
                        }}>
                          +49 2241 261310
                        </a>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <Mail size={28} style={{ color: 'var(--accent-blue)' }} />
                      <div>
                        <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          E-Mail
                        </div>
                        <a href="mailto:support@ab-informatik.net" style={{ 
                          fontSize: '18px', 
                          fontWeight: 600, 
                          color: 'var(--text-primary)',
                          textDecoration: 'none'
                        }}>
                          support@ab-informatik.net
                        </a>
                      </div>
                    </div>
                  </div>

                  <div style={{
                    padding: '16px',
                    background: 'rgba(255, 59, 48, 0.1)',
                    borderLeft: '4px solid #ff3b30',
                    borderRadius: '8px',
                    marginBottom: '24px'
                  }}>
                    <p style={{ fontSize: '14px', margin: 0, color: 'var(--text-primary)' }}>
                      <strong>24/7 Erreichbarkeit</strong> - Wir sind rund um die Uhr für Sie da und reagieren umgehend auf Sicherheitsvorfälle.
                    </p>
                  </div>

                  <Button 
                    className="btn-primary btn-full" 
                    onClick={() => window.location.href = 'tel:+492224989037'}
                    style={{ fontSize: '16px', padding: '14px' }}
                  >
                    <Phone size={20} />
                    Jetzt anrufen
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Cloud Phone System Modal */}
          {showPhoneModal && (
            <div className="modal-overlay" onClick={() => setShowPhoneModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowPhoneModal(false)}>
                  <X size={24} />
                </button>
                <div className="modal-header">
                  <div className="modal-icon">
                    <PhoneCall size={32} />
                  </div>
                  <h2 className="display-medium">{t('modal.phone.title')}</h2>
                </div>
                <div className="modal-body">
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.phone.description')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.phone.paragraph1')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    {t('modal.phone.paragraph2')}
                  </p>
                  <Button 
                    className="btn-primary btn-full" 
                    onClick={() => {
                      setShowPhoneModal(false);
                      scrollToSection('contact');
                    }}
                  >
                    {t('modal.phone.contact')}
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Interim IT Management Modal */}
          {showInterimModal && (
            <div className="modal-overlay" onClick={() => setShowInterimModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowInterimModal(false)}>
                  <X size={24} />
                </button>
                <div className="modal-header">
                  <div className="modal-icon">
                    <Users size={32} />
                  </div>
                  <h2 className="display-medium">{t('modal.interim.title')}</h2>
                </div>
                <div className="modal-body">
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.interim.description')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    {t('modal.interim.paragraph1')}
                  </p>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    {t('modal.interim.paragraph2')}
                  </p>
                  <Button 
                    className="btn-primary btn-full" 
                    onClick={() => {
                      setShowInterimModal(false);
                      scrollToSection('contact');
                    }}
                  >
                    {t('modal.interim.contact')}
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* IT Strategy Consulting Modal */}
          {showStrategyModal && (
            <div className="modal-overlay" onClick={() => setShowStrategyModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowStrategyModal(false)}>
                  <X size={24} />
                </button>
                <div className="modal-header">
                  <div className="modal-icon">
                    <Target size={32} />
                  </div>
                  <h2 className="display-medium">{t('modal.strategy.title')}</h2>
                </div>
                <div className="modal-body">
                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    {t('modal.strategy.description')}
                  </p>
                  <Button 
                    className="btn-primary btn-full" 
                    onClick={() => {
                      setShowStrategyModal(false);
                      scrollToSection('contact');
                    }}
                  >
                    {t('modal.strategy.contact')}
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Careers Section */}
      <section id="careers" className="careers-section">
        <div className="section-content">
          <h2 className="display-large section-title">{t('careers.title')}</h2>
          <p className="body-medium section-subtitle">{t('careers.subtitle')}</p>
          
          {/* Benefits */}
          <div className="benefits-grid">
            {companyData.careers.benefits.map((benefit) => {
              const Icon = careerIconMap[benefit.icon];
              const benefitKeys = ['flexible', 'training', 'projects', 'perspective'];
              const translationKey = benefitKeys[benefit.id - 1];
              return (
                <div key={benefit.id} className="benefit-card">
                  <div className="benefit-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="heading-2">{t(`careers.benefits.${translationKey}.title`)}</h3>
                  <p className="body-medium">{t(`careers.benefits.${translationKey}.description`)}</p>
                </div>
              );
            })}
          </div>
          
          {/* Open Positions */}
          <div className="positions-section">
            <h3 className="display-medium positions-title">{t('careers.openPositions')}</h3>
            <div className="positions-grid">
              {companyData.careers.openPositions.map((position) => {
                const jobKeys = ['systemEngineer', 'salesSupport', 'apprentice'];
                const translationKey = jobKeys[position.id - 1];
                return (
                  <div key={position.id} className="position-card">
                    <div className="position-header">
                      <div className="position-icon">
                        <Briefcase size={24} />
                      </div>
                      <div className="position-meta">
                        <span className="position-location">{t(`careers.jobs.${translationKey}.location`)}</span>
                        <span className="position-type">{t(`careers.jobs.${translationKey}.type`)}</span>
                      </div>
                    </div>
                    <h4 className="heading-2">{t(`careers.jobs.${translationKey}.title`)}</h4>
                    <p className="body-medium">{t(`careers.jobs.${translationKey}.description`)}</p>
                    <Button 
                      className="btn-secondary btn-position"
                      onClick={() => openApplicationModal(t(`careers.jobs.${translationKey}.title`))}
                    >
                      {t('careers.applyNow')}
                      <ChevronRight size={20} />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Application Modal */}
        {showApplicationModal && (
          <div className="modal-overlay" onClick={closeApplicationModal}>
            <div className="modal-content application-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeApplicationModal}>
                <X size={24} />
              </button>
              <div className="modal-header">
                <div className="modal-icon">
                  <Briefcase size={32} />
                </div>
                <h2 className="display-medium">{t('careers.applicationForm.title')}</h2>
                <p className="body-medium">{t('careers.applicationForm.subtitle')}</p>
              </div>
              <div className="modal-body">
                {applicationStatus.success ? (
                  <div className="success-message">
                    <div className="success-icon">✓</div>
                    <p>{t('careers.applicationForm.success')}</p>
                  </div>
                ) : (
                  <form onSubmit={handleApplicationSubmit} className="application-form">
                    <div className="form-group">
                      <label htmlFor="app-position">{t('careers.applicationForm.position')}</label>
                      <input
                        type="text"
                        id="app-position"
                        name="position"
                        value={applicationData.position}
                        disabled
                        className="form-control"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="app-name">{t('careers.applicationForm.name')} *</label>
                      <input
                        type="text"
                        id="app-name"
                        name="name"
                        value={applicationData.name}
                        onChange={handleApplicationChange}
                        placeholder={t('careers.applicationForm.namePlaceholder')}
                        required
                        className="form-control"
                        style={{ backgroundColor: '#f0f0f0', color: '#000000' }}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="app-email">{t('careers.applicationForm.email')} *</label>
                      <input
                        type="email"
                        id="app-email"
                        name="email"
                        value={applicationData.email}
                        onChange={handleApplicationChange}
                        placeholder={t('careers.applicationForm.emailPlaceholder')}
                        required
                        className="form-control"
                        style={{ backgroundColor: '#f0f0f0', color: '#000000' }}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="app-phone">{t('careers.applicationForm.phone')} *</label>
                      <input
                        type="tel"
                        id="app-phone"
                        name="phone"
                        value={applicationData.phone}
                        onChange={handleApplicationChange}
                        placeholder={t('careers.applicationForm.phonePlaceholder')}
                        required
                        className="form-control"
                        style={{ backgroundColor: '#f0f0f0', color: '#000000' }}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="app-message">{t('careers.applicationForm.message')} *</label>
                      <textarea
                        id="app-message"
                        name="message"
                        value={applicationData.message}
                        onChange={handleApplicationChange}
                        placeholder={t('careers.applicationForm.messagePlaceholder')}
                        required
                        rows="5"
                        className="form-control"
                        style={{ backgroundColor: '#f0f0f0', color: '#000000' }}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="app-resume">{t('careers.applicationForm.resume')} *</label>
                      <div className="file-upload-wrapper">
                        <input
                          type="file"
                          id="app-resume"
                          name="resume"
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx"
                          required
                          className="form-control-file"
                        />
                        <label htmlFor="app-resume" className="file-upload-label">
                          {applicationData.resume ? applicationData.resume.name : t('careers.applicationForm.resumeUpload')}
                        </label>
                      </div>
                      <small className="form-text">{t('careers.applicationForm.resumeFormats')}</small>
                    </div>
                    
                    {applicationStatus.error && (
                      <div className="error-message">
                        {applicationStatus.error}
                      </div>
                    )}
                    
                    <Button 
                      type="submit" 
                      className="btn-primary btn-full"
                      disabled={applicationStatus.loading}
                    >
                      {applicationStatus.loading ? t('careers.applicationForm.sending') : t('careers.applicationForm.submit')}
                      {!applicationStatus.loading && <ChevronRight size={20} />}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="section-content">
          <div className="about-grid">
            <div className="about-text">
              <h2 className="display-large">{t('company.title')}</h2>
              <p className="body-large">
                {t('company.description1')}
              </p>
              <p className="body-medium">
                {t('company.description2')}
              </p>
            </div>
            <div className="about-visual">
              <div className="datacenter-image-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_infoglas-app/artifacts/ffmbmw1i_generated-image.png" 
                  alt="Professionelle Server-Racks in modernem Rechenzentrum"
                  className="datacenter-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-content">
          <h2 className="display-large section-title">{t('contact.title')}</h2>
          <p className="body-medium section-subtitle">{t('contact.subtitle')}</p>
          
          {/* Google Maps Banner */}
          <div className="maps-banner">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2519.5847867890626!2d7.202476876738491!3d50.80129007167073!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bee3bfdc89db01%3A0x8c1f8b5e5c5f8c5e!2sAm%20Turm%2026%2C%2053721%20Siegburg%2C%20Germany!5e0!3m2!1sde!2sde!4v1640000000000!5m2!1sde!2sde"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="AB-Informatik GmbH Standort"
            />
          </div>
          
          <div className="contact-grid">
            <div className="contact-info">
              <div className="contact-item">
                <Mail size={24} className="contact-icon" />
                <div>
                  <div className="contact-label">{t('contact.email')}</div>
                  <a href={`mailto:${companyData.contact.email}`} className="contact-value">{companyData.contact.email}</a>
                </div>
              </div>
              
              <div className="contact-item">
                <Phone size={24} className="contact-icon" />
                <div>
                  <div className="contact-label">{t('contact.phone')}</div>
                  <a href={`tel:${companyData.contact.phone}`} className="contact-value">{companyData.contact.phone}</a>
                </div>
              </div>
              
              <div className="contact-item">
                <MapPin size={24} className="contact-icon" />
                <div>
                  <div className="contact-label">{t('contact.address')}</div>
                  <div className="contact-value">{companyData.contact.address}</div>
                </div>
              </div>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              {formStatus.success && (
                <div style={{
                  padding: '15px',
                  marginBottom: '20px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  ✓ Vielen Dank für Ihre Nachricht! Wir werden uns so schnell wie möglich bei Ihnen melden.
                </div>
              )}
              
              {formStatus.error && (
                <div style={{
                  padding: '15px',
                  marginBottom: '20px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  ✗ {formStatus.error}
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="name" className="form-label">Name*</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={formStatus.loading}
                  className="form-input"
                  placeholder="Ihr vollständiger Name"
                  autoComplete="name"
                  inputMode="text"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email" className="form-label">E-Mail*</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={formStatus.loading}
                  className="form-input"
                  placeholder="ihre@email.de"
                  autoComplete="email"
                  inputMode="email"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="phone" className="form-label">Telefon</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={formStatus.loading}
                  className="form-input"
                  placeholder="+49 123 456789"
                  autoComplete="tel"
                  inputMode="tel"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Wie können wir Ihnen helfen?*</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '12px',
                    background: formData.helpType === 'emergency' ? 'var(--bg-tertiary)' : 'transparent',
                    borderRadius: '8px',
                    border: `2px solid ${formData.helpType === 'emergency' ? 'var(--accent-blue)' : 'var(--border-subtle)'}`,
                    transition: 'all 0.2s ease'
                  }}>
                    <input
                      type="radio"
                      name="helpType"
                      value="emergency"
                      checked={formData.helpType === 'emergency'}
                      onChange={handleChange}
                      disabled={formStatus.loading}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>🚨 IT-Notfall (24h-Rückruf)</span>
                  </label>
                  
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '12px',
                    background: formData.helpType === 'consultation' ? 'var(--bg-tertiary)' : 'transparent',
                    borderRadius: '8px',
                    border: `2px solid ${formData.helpType === 'consultation' ? 'var(--accent-blue)' : 'var(--border-subtle)'}`,
                    transition: 'all 0.2s ease'
                  }}>
                    <input
                      type="radio"
                      name="helpType"
                      value="consultation"
                      checked={formData.helpType === 'consultation'}
                      onChange={handleChange}
                      disabled={formStatus.loading}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>📅 Beratungstermin vereinbaren</span>
                  </label>
                  
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '12px',
                    background: formData.helpType === 'quote' ? 'var(--bg-tertiary)' : 'transparent',
                    borderRadius: '8px',
                    border: `2px solid ${formData.helpType === 'quote' ? 'var(--accent-blue)' : 'var(--border-subtle)'}`,
                    transition: 'all 0.2s ease'
                  }}>
                    <input
                      type="radio"
                      name="helpType"
                      value="quote"
                      checked={formData.helpType === 'quote'}
                      onChange={handleChange}
                      disabled={formStatus.loading}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>💼 Angebot anfragen</span>
                  </label>
                  
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '12px',
                    background: formData.helpType === 'other' ? 'var(--bg-tertiary)' : 'transparent',
                    borderRadius: '8px',
                    border: `2px solid ${formData.helpType === 'other' ? 'var(--accent-blue)' : 'var(--border-subtle)'}`,
                    transition: 'all 0.2s ease'
                  }}>
                    <input
                      type="radio"
                      name="helpType"
                      value="other"
                      checked={formData.helpType === 'other'}
                      onChange={handleChange}
                      disabled={formStatus.loading}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>💬 Sonstiges</span>
                  </label>
                </div>
              </div>
              
              {formData.helpType && (
                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    {formData.helpType === 'other' ? 'Ihre Nachricht*' : 'Zusätzliche Informationen (optional)'}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required={formData.helpType === 'other'}
                    disabled={formStatus.loading}
                    rows="4"
                    className="form-input"
                    placeholder={
                      formData.helpType === 'emergency' ? 'Beschreiben Sie kurz den IT-Notfall...' :
                      formData.helpType === 'consultation' ? 'Wann wären Sie verfügbar? Welche Themen möchten Sie besprechen?' :
                      formData.helpType === 'quote' ? 'Für welche Services benötigen Sie ein Angebot?' :
                      'Beschreiben Sie kurz Ihr Anliegen...'
                    }
                  />
                </div>
              )}
              
              <Button 
                type="submit" 
                className="btn-primary btn-full"
                disabled={formStatus.loading || !formData.helpType}
              >
                {formStatus.loading ? 'Wird gesendet...' : 'Absenden'}
                <ChevronRight size={20} />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-content">
          <h2 className="display-large section-title">{t('testimonials.title')}</h2>
          <p className="body-medium section-subtitle">
            {t('testimonials.subtitle')}
          </p>
          
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                "{t('testimonials.items.weber.text')}"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">MW</div>
                <div className="author-info">
                  <div className="author-name">{t('testimonials.items.weber.name')}</div>
                  <div className="author-position">{t('testimonials.items.weber.position')}</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                "{t('testimonials.items.klein.text')}"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">SK</div>
                <div className="author-info">
                  <div className="author-name">{t('testimonials.items.klein.name')}</div>
                  <div className="author-position">{t('testimonials.items.klein.position')}</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                "{t('testimonials.items.mueller.text')}"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">TM</div>
                <div className="author-info">
                  <div className="author-name">{t('testimonials.items.mueller.name')}</div>
                  <div className="author-position">{t('testimonials.items.mueller.position')}</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                "{t('testimonials.items.hoffmann.text')}"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">JH</div>
                <div className="author-info">
                  <div className="author-name">{t('testimonials.items.hoffmann.name')}</div>
                  <div className="author-position">{t('testimonials.items.hoffmann.position')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Partners Section */}
      <section className="partners-section">
        <div className="section-content">
          <h2 className="display-large section-title">{t('partners.title')}</h2>
          <p className="body-medium section-subtitle">
            {t('partners.subtitle')}
          </p>
          <div className="partners-grid">
            <div className="partner-logo">
              <img src="https://customer-assets.emergentagent.com/job_ab-consulting/artifacts/ghe17waf_Microsoft_365_logo.png" alt="Microsoft 365" />
            </div>
            <div className="partner-logo">
              <img src="https://upload.wikimedia.org/wikipedia/commons/1/18/Dell_logo_2016.svg" alt="Dell" />
            </div>
            <div className="partner-logo proxmox-logo">
              <img src="https://customer-assets.emergentagent.com/job_it-tetris-consult/artifacts/jp6irywr_Proxmox_logo_standard_hex_400px.png" alt="Proxmox" />
            </div>
            <div className="partner-logo">
              <img src="https://customer-assets.emergentagent.com/job_ab-consulting/artifacts/t0ip62ge_logo-3-1.png" alt="3CX" />
            </div>
            <div className="partner-logo">
              <img src="https://customer-assets.emergentagent.com/job_ab-consulting/artifacts/rmowx0m0_logo_hornetsecurity-cloud-security.png" alt="Hornetsecurity" />
            </div>
            <div className="partner-logo datacore-logo">
              <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/uxitnlld_PartnerCertifications-SPECIALIST.png" alt="DataCore Specialist" />
            </div>
            <div className="partner-logo sophos-logo">
              <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/yfhsw4vq_sophos-partner-portal.png" alt="Sophos Partner" />
            </div>
            <div className="partner-logo fujitsu-logo">
              <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/04qc3qqm_logo_tcm137-871390.gif" alt="Fujitsu" />
            </div>
            <div className="partner-logo veeam-logo">
              <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/71acd174_Veeam-logo.png" alt="Veeam" />
            </div>
            <div className="partner-logo lenovo-logo">
              <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/ttew0w55_8hjmcte754uauw07ypikjkjtx0m5ib450914.svg" alt="Lenovo" />
            </div>
            <div className="partner-logo sentinelone-logo">
              <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/61tszyv1_header-logo-dark.svg" alt="SentinelOne" />
            </div>
            <div className="partner-logo c2-logo">
              <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/nhs3ymgf_top-C2Logo-2021.png" alt="Code Two" />
            </div>
            <div className="partner-logo omnissa-logo">
              <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/ys3eg5vs_Omnissa_TM-Logo-CLR-RGB-cropped-1.jpg" alt="Omnissa" />
            </div>
            <div className="partner-logo ubiquiti-logo">
              <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/jzsis5cx_UBNT_Alternate_Logo_RGB.jpg" alt="Ubiquiti" />
            </div>
            <div className="partner-logo yealink-logo">
              <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/c92s305u_yealink-logo.png" alt="Yealink" />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/32p3ysgt_AB-INFORMATIK-Logo_Verlauf_2025%20neu.png" alt="AB-Informatik Logo" className="footer-logo-img logo-dark-mode" />
          <img src="https://customer-assets.emergentagent.com/job_landing-optimization-1/artifacts/dw1boizi_AB-INFORMATIK-Logo_Verlauf_2025.png" alt="AB-Informatik Logo" className="footer-logo-img logo-light-mode" />
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: '12px',
            width: '100%',
            maxWidth: '400px',
            margin: '20px auto 16px auto'
          }}>
            <button 
              onClick={() => setShowImprintModal(true)} 
              style={{
                width: '100%',
                color: 'rgba(255, 255, 255, 0.6)',
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '400',
                padding: '10px 20px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                minHeight: '40px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z"/>
                <path d="M5 6h6M5 9h6M5 12h3"/>
              </svg>
              {t('cookie.imprint')}
            </button>
            <button 
              onClick={() => setShowPrivacyModal(true)} 
              style={{
                width: '100%',
                color: 'rgba(255, 255, 255, 0.6)',
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '400',
                padding: '10px 20px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                minHeight: '40px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 1L3 3v4c0 3.5 2 6.5 5 8 3-1.5 5-4.5 5-8V3l-5-2z"/>
                <path d="M6 8l1.5 1.5L11 6"/>
              </svg>
              {t('cookie.privacy')}
            </button>
          </div>
          <div className="footer-text">© 2025 AB-Informatik GmbH. {t('footer.rights')}</div>
        </div>
      </footer>

      {/* Imprint Modal */}
      {showImprintModal && (
        <div className="modal-overlay" onClick={() => setShowImprintModal(false)}>
          <div className="modal-content imprint-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowImprintModal(false)}>
              <X size={24} />
            </button>
            <div className="modal-header">
              <h2 className="display-medium">{t('imprint.title')}</h2>
            </div>
            <div className="modal-body imprint-content">
              <div className="imprint-section">
                <h3 className="heading-3">Angaben gemäß § 5 DDG (Digital Services Act)</h3>
                <p className="body-medium">
                  <strong>AB-Informatik GmbH</strong><br />
                  Am Turm 26<br />
                  53721 Siegburg<br />
                  Deutschland<br />
                  <br />
                  Tel.: <a href="tel:+492241261310">+49 2241 261310</a><br />
                  E-Mail: <a href="mailto:info@ab-informatik.net">info@ab-informatik.net</a><br />
                  Website: <a href="https://www.ab-informatik.net">www.ab-informatik.net</a>
                </p>
              </div>

              <div className="imprint-section">
                <h3 className="heading-3">Vertretungsberechtigte Geschäftsführer</h3>
                <p className="body-medium">Artur Borgart, Stefan Appenburg</p>
              </div>

              <div className="imprint-section">
                <h3 className="heading-3">Registereintrag</h3>
                <p className="body-medium">
                  <strong>Registergericht:</strong> Amtsgericht Siegburg<br />
                  <strong>Registernummer:</strong> HRB 16570<br />
                  <strong>Umsatzsteuer-ID:</strong> DE314921436<br />
                  <strong>Steuernummer:</strong> 32/660/15034
                </p>
              </div>

              <div className="imprint-section">
                <h3 className="heading-3">Berufsbezeichnung</h3>
                <p className="body-medium">
                  IT-Dienstleistungen und IT-Beratung<br />
                  Erbringung von Dienstleistungen der Informationstechnologie
                </p>
              </div>

              <div className="imprint-section">
                <h3 className="heading-3">Verantwortlich für den Inhalt gemäß § 55 Abs. 2 RStV</h3>
                <p className="body-medium">
                  Artur Borgart<br />
                  AB-Informatik GmbH<br />
                  Am Turm 26, 53721 Siegburg
                </p>
              </div>

              <div className="imprint-section">
                <h3 className="heading-3">Alternative Streitbeilegung</h3>
                <p className="body-medium">
                  Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: 
                  <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer"> https://ec.europa.eu/consumers/odr</a><br />
                  <br />
                  Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
                </p>
              </div>

              <div className="imprint-section">
                <h3 className="heading-3">Haftung für Inhalte</h3>
                <p className="body-medium">
                  Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. 
                  Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu 
                  überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder 
                  Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt.
                </p>
              </div>

              <div className="imprint-section">
                <h3 className="heading-3">Haftung für Links</h3>
                <p className="body-medium">
                  Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für 
                  diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder 
                  Betreiber der Seiten verantwortlich. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.
                </p>
              </div>

              <div className="imprint-section">
                <h3 className="heading-3">Urheberrecht</h3>
                <p className="body-medium">
                  Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. 
                  Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes 
                  bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
                </p>
              </div>

              <Button 
                className="btn-secondary btn-full" 
                onClick={() => setShowImprintModal(false)}
              >
                {t('imprint.close')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="modal-content imprint-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPrivacyModal(false)}>
              <X size={24} />
            </button>
            <div className="modal-header">
              <h2 className="display-medium">{t('privacy.title')}</h2>
            </div>
            <div className="modal-body imprint-content">
              <div 
                className="privacy-content"
                dangerouslySetInnerHTML={{ __html: i18n.language === 'de' ? privacyContent.de : privacyContent.en }}
              />
              <Button 
                className="btn-secondary btn-full" 
                onClick={() => setShowPrivacyModal(false)}
                style={{ marginTop: '24px' }}
              >
                {t('privacy.close')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Help Widget (replaces old floating buttons) */}
      <HelpWidget />

      {/* Security Risk Assessment Modal */}
      <SecurityRiskAssessment 
        isOpen={showSecurityAssessment} 
        onClose={() => setShowSecurityAssessment(false)}
        onOpenChat={() => setShowAIChat(true)}
      />

      {/* AI Chat */}
      <AIChat ref={aiChatRef} isOpen={showAIChat} setIsOpen={setShowAIChat} />
    </div>
  );
};

export default LandingPage;